import { useMemo } from "react"
import { useInfiniteQuery, useQuery } from "react-query"
import {
  DistinctPersonValues, getDistinctPersonValues, getGuest, getGuests,
} from "api/guests"
import { Filter, Guest, GuestSummary } from "sharedTypes"
import { serializeFilters } from "utilities/filterRulesUrl"

const EMPTY_DISTINCT: DistinctPersonValues = { company: [], jobTitle: [] }

export const guestCacheKey = (guestId?: string) => (guestId ? ["guest", guestId] : "guest")
export const guestsCacheKey = (
  eventId: string,
  searchString?: string,
  groupId?: string | number,
  order?: string,
  segmentId?: string,
  filters?: Filter[],
) => {
  // Cache by the chip set whenever it's present — including the case where a
  // segment is also active and the chips diverge from segment.rules. Without
  // this, mutating a chip on an active segment hits the same cache key as
  // the clean segment view and the table doesn't refetch.
  const filtersKey = filters && filters.length > 0
    ? serializeFilters(filters)
    : undefined

  return ["guests", eventId, searchString, groupId, order, segmentId, filtersKey].filter(Boolean)
}

export const useGuest = (
  eventId: string,
  guestId: string,
):Guest => useQuery(
  guestCacheKey(guestId),
  () => getGuest(eventId, guestId).then(({ data }) => data),
  {
    initialData: {} as Guest,
  },
).data as Guest

export const useDistinctPersonValues = (
  eventId: string,
): DistinctPersonValues => useQuery(
  ["distinctPersonValues", eventId],
  () => getDistinctPersonValues(eventId).then(({ data }) => data),
  {
    // Values change infrequently; a 5-minute stale time avoids redundant
    // requests while keeping pickers reasonably fresh.
    staleTime: 5 * 60 * 1000,
  },
).data ?? EMPTY_DISTINCT

export const useGuests = (
  eventId,
  searchString?,
  order?,
  groupId?,
  segmentId?,
  filters?: Filter[],
) => {
  const {
    data, fetchNextPage, isLoading, isFetchingNextPage,
  } = useInfiniteQuery(
    guestsCacheKey(eventId, searchString, groupId, order, segmentId, filters),
    ({ pageParam = 1 }) => getGuests(
      eventId, pageParam, searchString, groupId, order, segmentId, filters,
    ),
    {
      getNextPageParam: (
        lastPage,
        pages,
      ) => (lastPage.data.length === 100 ? pages.length + 1 : undefined),
    },
  )

  const guests: GuestSummary[] = useMemo(
    // A page's data can be "" (an empty response body): spreading it adds nothing
    () => data?.pages.reduce((prev, curr) => ([...prev, ...curr.data]), [] as GuestSummary[]) || [],
    [data],
  )

  return {
    guests, fetchNextPage, isLoading, isFetchingNextPage,
  }
}
